/**
 * Player Profile Modal Component
 *
 * DATE: 2025-12-17 | Author: IA + Luiz Couto
 *
 * Modal robusto para exibir perfil de jogador com verificação de amizade.
 * Substitui o Tippy.js por uma solução mais controlada.
 *
 * Regras:
 * - "Enviar mensagem" só aparece se forem amigos
 * - "Convidar Amizade" aparece se não forem amigos
 * - "Criar Conta" aparece se o usuário atual não tem conta
 * - "Sem conta" aparece se o alvo não tem conta
 */

import { checkFriendshipStatus, sendFriendInvite, FriendshipStatus } from '../api/friendsApi';

// ============================================================================
// TIPOS
// ============================================================================

export interface PlayerData {
  id: string;
  name: string;
  netcartasId?: number | null;
  profileImageURL?: string | null;
  isAI?: boolean;
  isOnline?: boolean;
}

/**
 * Dados para ação pendente após criação de conta
 * DATE: 2025-12-20 | Author: IA + Luiz Couto
 */
export interface PendingActionData {
  type: 'add_friend' | 'view_ranking' | 'save_stats';
  contextMessage: string;
  targetPlayer?: PlayerData;
}

export interface ModalCallbacks {
  onSendMessage?: (player: PlayerData) => void;
  onCreateAccountWithAction?: (pendingAction: PendingActionData) => void;
  onClose?: () => void;
}

// ============================================================================
// MODAL CLASS
// ============================================================================

export class PlayerProfileModal {
  private modalElement: HTMLElement | null = null;
  private currentPlayer: PlayerData | null = null;
  private myNetcartasId: number | null = null;
  private myNickname: string = '';
  private callbacks: ModalCallbacks = {};

  constructor() {
    this.createModalElement();
    this.setupEventListeners();
  }

  /**
   * Define a identidade do usuário atual
   */
  public setMyIdentity(netcartasId: number | null, nickname: string): void {
    this.myNetcartasId = netcartasId;
    this.myNickname = nickname;
    console.log(`👤 [2025-12-17] PlayerProfileModal: Identity set - ${nickname} (${netcartasId})`);
  }

  /**
   * Define callbacks para ações do modal
   */
  public setCallbacks(callbacks: ModalCallbacks): void {
    this.callbacks = callbacks;
  }

  /**
   * Abre o modal para um jogador específico
   */
  public async open(playerData: PlayerData): Promise<void> {
    console.log(`📖 [2025-12-17] Opening profile modal for: ${playerData.name}`);

    this.currentPlayer = playerData;

    // Mostra modal com estado de carregamento
    this.render('loading');
    this.show();

    // Verifica status de amizade
    const friendshipStatus = await this.checkFriendship(playerData);

    // Atualiza modal com status real
    this.render(friendshipStatus);
  }

  /**
   * Fecha o modal
   */
  public close(): void {
    this.hide();
    this.currentPlayer = null;
    this.callbacks.onClose?.();
  }

  // ============================================================================
  // PRIVATE METHODS
  // ============================================================================

  /**
   * Cria o elemento do modal no DOM
   */
  private createModalElement(): void {
    // Remove modal existente se houver
    const existing = document.getElementById('player-profile-modal');
    if (existing) {
      existing.remove();
    }

    // Cria novo modal
    const modal = document.createElement('div');
    modal.id = 'player-profile-modal';
    modal.className = 'player-profile-modal hidden';
    modal.innerHTML = `
      <div class="player-profile-modal-backdrop"></div>
      <div class="player-profile-modal-content">
        <button class="player-profile-modal-close" aria-label="Fechar">&times;</button>
        <div class="player-profile-modal-body"></div>
      </div>
    `;

    document.body.appendChild(modal);
    this.modalElement = modal;
  }

  /**
   * Configura event listeners
   */
  private setupEventListeners(): void {
    if (!this.modalElement) return;

    // Fechar ao clicar no backdrop
    const backdrop = this.modalElement.querySelector('.player-profile-modal-backdrop');
    backdrop?.addEventListener('click', () => this.close());

    // Fechar ao clicar no X
    const closeBtn = this.modalElement.querySelector('.player-profile-modal-close');
    closeBtn?.addEventListener('click', () => this.close());

    // Fechar ao pressionar ESC
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && this.isVisible()) {
        this.close();
      }
    });
  }

  /**
   * Verifica status de amizade com o jogador
   */
  private async checkFriendship(playerData: PlayerData): Promise<FriendshipStatus | 'self' | 'no_account' | 'target_no_account'> {
    // É o próprio jogador?
    if (playerData.name === this.myNickname) {
      console.log(`🔍 [2025-12-17] Status: self`);
      return 'self';
    }

    // Eu não tenho conta?
    if (!this.myNetcartasId) {
      console.log(`🔍 [2025-12-17] Status: no_account (I don't have NetCartas ID)`);
      return 'no_account';
    }

    // O alvo não tem conta?
    if (!playerData.netcartasId) {
      console.log(`🔍 [2025-12-17] Status: target_no_account (${playerData.name} has no NetCartas ID)`);
      return 'target_no_account';
    }

    // Ambos têm conta - verificar amizade via API
    console.log(`🔍 [2025-12-17] Checking friendship via API: ${this.myNetcartasId} -> ${playerData.netcartasId}`);
    return await checkFriendshipStatus(this.myNetcartasId, playerData.netcartasId);
  }

  /**
   * Renderiza o conteúdo do modal
   */
  private render(status: string): void {
    if (!this.modalElement || !this.currentPlayer) return;

    const body = this.modalElement.querySelector('.player-profile-modal-body');
    if (!body) return;

    const player = this.currentPlayer;

    // Gerar iniciais
    const initials = player.name
      .split(' ')
      .map(word => word[0])
      .join('')
      .toUpperCase()
      .substring(0, 2);

    // Avatar HTML
    const avatarHtml = player.profileImageURL
      ? `<img src="${player.profileImageURL}" alt="${player.name}" class="profile-modal-avatar-img" />`
      : `<div class="profile-modal-avatar-initials">${initials}</div>`;

    // Status online
    const onlineStatus = player.isOnline !== false ? 'Online' : 'Offline';
    const onlineClass = player.isOnline !== false ? 'online' : 'offline';

    // AI badge
    const aiBadge = player.isAI ? '<span class="profile-modal-ai-badge">AI</span>' : '';

    // Botões baseados no status
    const buttonsHtml = this.renderButtons(status);

    body.innerHTML = `
      <div class="profile-modal-header">
        <div class="profile-modal-avatar">
          ${avatarHtml}
        </div>
        <div class="profile-modal-info">
          <div class="profile-modal-name">${player.name} ${aiBadge}</div>
          <div class="profile-modal-status ${onlineClass}">
            <span class="status-dot"></span> ${onlineStatus}
          </div>
        </div>
      </div>

      <div class="profile-modal-stats">
        <div class="profile-modal-stat">
          <div class="stat-value">0</div>
          <div class="stat-label">Ranking</div>
        </div>
        <div class="profile-modal-stat">
          <div class="stat-value">0</div>
          <div class="stat-label">Jogos</div>
        </div>
        <div class="profile-modal-stat">
          <div class="stat-value">0</div>
          <div class="stat-label">Vitórias</div>
        </div>
      </div>

      <div class="profile-modal-actions">
        ${buttonsHtml}
      </div>
    `;

    // Setup button handlers
    this.setupButtonHandlers(status);
  }

  /**
   * Renderiza os botões baseado no status de amizade
   */
  private renderButtons(status: string): string {
    if (status === 'loading') {
      return `
        <button class="profile-modal-btn profile-modal-btn-secondary" disabled>
          <span class="btn-spinner"></span> Verificando...
        </button>
      `;
    }

    if (status === 'self') {
      // É o próprio usuário, não mostra botões de amizade
      return '';
    }

    let friendButton = '';
    let messageButton = '';

    switch (status) {
      case 'friends':
        // São amigos - mostra "Enviar mensagem"
        friendButton = `
          <button class="profile-modal-btn profile-modal-btn-success" disabled>
            ✓ Amigo
          </button>
        `;
        messageButton = `
          <button class="profile-modal-btn profile-modal-btn-primary" data-action="send-message">
            Enviar mensagem
          </button>
        `;
        break;

      case 'pending_sent':
        // DATE: 2025-12-18 | Author: IA + Luiz Couto
        // Added green checkmark icon for visual confirmation that invite was sent
        friendButton = `
          <button class="profile-modal-btn profile-modal-btn-success" disabled>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round" stroke-linejoin="round">
              <polyline points="20 6 9 17 4 12"></polyline>
            </svg>
            Convite Enviado
          </button>
        `;
        // Não mostra "Enviar mensagem" - não são amigos ainda
        break;

      case 'pending_received':
        friendButton = `
          <button class="profile-modal-btn profile-modal-btn-primary" data-action="accept-friend">
            Aceitar Amizade
          </button>
        `;
        // Não mostra "Enviar mensagem" - não são amigos ainda
        break;

      case 'not_friends':
        friendButton = `
          <button class="profile-modal-btn profile-modal-btn-invite" data-action="invite-friend">
            Adicionar Amigo
          </button>
        `;
        // Não mostra "Enviar mensagem" - não são amigos
        break;

      case 'no_account':
        // DATE: 2025-12-20 | Author: IA + Luiz Couto
        // Mostra "Adicionar Amigo" mesmo sem conta - ao clicar, abre modal de criação de conta
        // com contexto "para adicionar este jogador como amigo"
        friendButton = `
          <button class="profile-modal-btn profile-modal-btn-invite" data-action="add-friend-needs-account">
            Adicionar Amigo
          </button>
        `;
        break;

      case 'target_no_account':
        friendButton = `
          <button class="profile-modal-btn profile-modal-btn-secondary" disabled>
            Sem conta
          </button>
        `;
        break;

      default:
        friendButton = `
          <button class="profile-modal-btn profile-modal-btn-invite" data-action="invite-friend">
            Adicionar Amigo
          </button>
        `;
    }

    return messageButton + friendButton;
  }

  /**
   * Configura handlers dos botões
   */
  private setupButtonHandlers(_status: string): void {
    if (!this.modalElement || !this.currentPlayer) return;

    const player = this.currentPlayer;

    // Enviar mensagem
    const sendMessageBtn = this.modalElement.querySelector('[data-action="send-message"]');
    sendMessageBtn?.addEventListener('click', () => {
      console.log(`💬 [2025-12-17] Send message to ${player.name}`);
      this.callbacks.onSendMessage?.(player);
      this.close();
    });

    // Convidar amizade
    // DATE: 2025-12-18 | Author: IA + Luiz Couto - Removed async/await for optimistic UI
    const inviteFriendBtn = this.modalElement.querySelector('[data-action="invite-friend"]');
    inviteFriendBtn?.addEventListener('click', () => {
      this.handleInviteFriend();
    });

    // Aceitar amizade
    const acceptFriendBtn = this.modalElement.querySelector('[data-action="accept-friend"]');
    acceptFriendBtn?.addEventListener('click', async () => {
      // TODO: Implementar aceitar amizade (precisa do saidId)
      console.log(`✅ [2025-12-17] Accept friend request from ${player.name}`);
      this.close();
    });

    // DATE: 2025-12-20 | Author: IA + Luiz Couto
    // Adicionar amigo quando usuário não tem conta
    // Abre modal de criação de conta com ação pendente de adicionar amigo
    const addFriendNeedsAccountBtn = this.modalElement.querySelector('[data-action="add-friend-needs-account"]');
    addFriendNeedsAccountBtn?.addEventListener('click', () => {
      console.log(`📝 [2025-12-20] Add friend needs account - will create account first`);
      
      const pendingAction: PendingActionData = {
        type: 'add_friend',
        contextMessage: `para adicionar ${player.name} como amigo`,
        targetPlayer: player
      };
      
      this.callbacks.onCreateAccountWithAction?.(pendingAction);
      this.close();
    });
  }

  /**
   * Handler para convidar amizade
   * DATE: 2025-12-18 | Author: IA + Luiz Couto
   * Implementa padrão "Optimistic UI":
   * - Atualiza UI imediatamente para "Convite enviado"
   * - Dispara request em background (fire-and-forget)
   * - Não bloqueia a UI esperando resposta do servidor
   * - Erros são logados no console (não afetam UX)
   */
  private handleInviteFriend(): void {
    if (!this.currentPlayer || !this.myNetcartasId || !this.currentPlayer.netcartasId) {
      console.error('❌ [2025-12-17] Cannot invite friend: missing IDs');
      return;
    }

    const targetPlayer = this.currentPlayer;
    const myId = this.myNetcartasId;
    const myNickname = this.myNickname;

    // DATE: 2025-12-18 | Author: IA + Luiz Couto
    // OPTIMISTIC UI: Atualiza interface IMEDIATAMENTE para "Convite enviado"
    // Não esperamos resposta do servidor - assumimos sucesso
    console.log(`📤 [2025-12-18] Sending friend invite (optimistic UI) to ${targetPlayer.name}`);
    this.render('pending_sent');

    // DATE: 2025-12-18 | Author: IA + Luiz Couto
    // FIRE-AND-FORGET: Dispara request em background
    // Erros são logados mas não afetam a UX (convite será processado eventualmente)
    sendFriendInvite(myId, targetPlayer.netcartasId!, myNickname)
      .then(result => {
        if (result.success) {
          console.log(`✅ [2025-12-18] Friend invite confirmed by server`);
        } else {
          // Log error but don't revert UI - invite may still be processed
          console.warn(`⚠️ [2025-12-18] Friend invite response: ${result.error}`);
        }
      })
      .catch(error => {
        // Log error but don't revert UI - network issues are often temporary
        console.error('❌ [2025-12-18] Error sending friend invite (background):', error);
      });
  }

  /**
   * Mostra o modal
   */
  private show(): void {
    this.modalElement?.classList.remove('hidden');
    document.body.style.overflow = 'hidden'; // Previne scroll do body
  }

  /**
   * Esconde o modal
   */
  private hide(): void {
    this.modalElement?.classList.add('hidden');
    document.body.style.overflow = ''; // Restaura scroll
  }

  /**
   * Verifica se o modal está visível
   */
  private isVisible(): boolean {
    return !this.modalElement?.classList.contains('hidden');
  }
}

// ============================================================================
// SINGLETON INSTANCE
// ============================================================================

let modalInstance: PlayerProfileModal | null = null;

export function getPlayerProfileModal(): PlayerProfileModal {
  if (!modalInstance) {
    modalInstance = new PlayerProfileModal();
  }
  return modalInstance;
}
